import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
    // Navbar chrome
    FaSignOutAlt,
    FaAngleLeft,
    FaAngleRight,
    FaGripLines,
    FaBars,
    FaChevronLeft,
    FaChevronUp,
    FaChevronDown,
    FaCamera,
    FaExternalLinkAlt,
    // Menu icons - one per entry, see NAV_CONFIG
    FaChartPie,
    FaHandsHelping,
    FaTicketAlt,
    FaDesktop,
    FaBug,
    FaWrench,
    FaLightbulb,
    FaWpforms,
    FaClock,
    FaSlidersH,
    FaProjectDiagram,
    FaCalendarAlt,
    FaCalendarCheck,
    FaLayerGroup,
    FaBusinessTime,
    FaHourglassHalf,
    FaUserClock,
    FaStopwatch,
    FaClipboardCheck,
    FaCalendarTimes,
    FaTachometerAlt,
    FaSitemap,
    FaCheckDouble,
    FaUmbrellaBeach,
    FaClipboardList,
    FaUserCheck,
    FaUserFriends,
    FaCalendarPlus,
    FaTasks,
    FaChartBar,
    FaEnvelopeOpenText,
    FaScroll,
    FaFileInvoiceDollar,
    FaCogs,
    FaMoneyCheckAlt,
    FaBolt,
    FaFolderOpen,
    FaFileAlt,
    FaIdCard,
    FaIdBadge,
    FaReceipt,
    FaMailBulk,
    FaNewspaper,
    FaFileSignature,
    FaBell,
    FaAddressBook,
    FaHistory,
    FaUsers,
    FaUserTie,
    FaUsersCog,
    FaUserShield,
    FaKey,
    FaUserLock,
    FaTools,
    FaGlobe,
    FaPaperPlane,
    FaImages,
    FaBullhorn,
    FaBlog,
    FaGavel,
    FaClone,
} from "react-icons/fa";
import { dnAlert } from "./utils/dNews_Alert";
import { useAccess } from "./utils/AccessContext";
import styled from 'styled-components';
import { apiFetch, API_BASE } from "./utils/api";
import logo_eagle from "./assets/img/logo_eagle.png";
import help_desk from "./assets/img/help_desk.png";
import { createPortal } from "react-dom";
import "./left_navbar.css";

const RoundedCircle = styled.span`
  border-radius: 50%;
  text-align: center;
  font-size: 14px;
  line-height: 40px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
`;

const DropdownMenu = styled.div`
  position: ${({ $navSize }) => ($navSize === "icon-only" ? "fixed" : "absolute")};
  bottom: ${({ $navSize }) => ($navSize === "icon-only" ? "0px" : "75px")};
  left: ${({ $navSize }) => ($navSize === "icon-only" ? "55px" : "2px")};
  background-color: white;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  padding: 10px 0;
  min-width: ${({ $navSize }) => ($navSize === "icon-only" ? "200px" : "216px")};
  z-index: 9999;
  display: ${({ $isOpen }) => ($isOpen ? "block" : "none")};
  transition: all 0.2s ease-in-out;
`;

/* Profile Section inside Dropdown */
const DropdownProfile = styled.div`
  padding: 10px 15px;
  display: flex;
  align-items: center; /* Align profile image and text */
  border-bottom: 1px solid #ddd;
`;

/* Profile Name */
const DropdownProfileName = styled.div`
  max-width: 140px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 1rem;
  font-weight: 600;
  color: #333;
  display: block;
`;

/* Profile Organization */
const DropdownProfileOrg = styled.div`
  font-size: 0.85rem;
  color: gray;
  margin-top: 3px;
  white-space: nowrap;
`;

/* Dropdown Item */
const DropdownItem = styled.a`
  padding: 12px 15px;
  display: block;
  font-size: 14px;
  color: #333;
  text-decoration: none;

  &:hover {
    color: hsl(8, 77%, 56%);
    background-color: #f8f9fa;
  }
`;

/* Divider */
const DropdownDivider = styled.hr`
  margin: 5px auto;
  width: 80%;
  border: 0;
  border-top: 2px solid #888;
`;

// ── Navigation tree ─────────────────────────────────────────────────────────
// Single source of truth for the whole menu. Everything else in this file is
// derived from it: the route→section map that drives the accordion, the set of
// collapsible section ids, and the rendered markup. Adding a page means adding
// ONE entry here - no other edit anywhere in this file.
//
// Node shapes:
//   section  { id, label, icon, category, children }   collapsible, top level
//   group    { id, label, icon, children }             collapsible, nested
//   route    { label, icon, to, page }                 internal <NavLink>
//   external { label, icon, href, page }               opens in a new tab
//
// `category` is inherited down the tree, so leaves only declare `page`. The
// pair (category, page) is exactly what Access Level Configuration stores in
// access_levels, so hasAccess() gates each leaf and hasAnyAccess() gates the
// top-level section - same rules as before, now expressed once.
//
// Icons: every entry has its OWN icon - no two rows share a glyph, so a row is
// identifiable from its shape alone in the 50px icon-only rail. The single
// deliberate exception is FaDesktop, used by every "User Interface" /
// "Admin Interface" external link, because those all mean the same thing.
//
// Colours - ONE rule, no exceptions: a row's colour is a tint of the hue owned
// by the collapsible group that directly contains it. So the hue tells you
// which module a row belongs to even after its header has scrolled off, while
// the tint keeps the row itself individually identifiable (every value in this
// file is unique).
//
// dTime is the single place where a NESTED group also owns a hue rather than
// inheriting dTime's blue. That is deliberate: dTime carries 12 rows over three
// levels, and 12 shades of one blue would be indistinguishable. Its four
// sub-group hues are drawn from the arc adjacent to blue (light-blue, cyan,
// violet) so they still read as one family. Sub-group children are tints of
// their own sub-group, and dTime's DIRECT children (Holiday, User Interface)
// are tints of dTime's blue.
//
// Every value here is generated inside a fixed HSL band - lightness 58-73%,
// saturation 62-83% - and that band is the point. Tints above ~80% lightness
// (the xxx-100/200 end of a normal colour scale) collapse to white against the
// dark navbar and lose the family cue entirely; below ~55% they sink into the
// background. Adding a row means picking a hue near its group's and staying
// inside the band, which also guarantees the >=3:1 contrast the icon needs once
// the 0.88 resting opacity in left_navbar.css is composited in.
//
// The value is piped into the row as the --icon-color CSS var and also tints
// the active indicator bar.
const NAV_CONFIG = [
    {
        label: "Dashboard", icon: FaChartPie, color: "#51acd6",
        to: "/Dashboard", category: "dAdmin", page: "Dashboard",
    },
    // ── dAttendance · blue ──────────────────────────────────────────────────
    // Placed directly below Dashboard for quick access.
    {
        id: "dAttendance", label: "dAttendance", icon: FaCalendarCheck, color: "#519dd6", category: "dAttendance",
        children: [
            { label: "Attendance Manager", icon: FaLayerGroup, color: "#4f97d0", to: "/dAttendance_Manage", page: "Work Pattern" },
            { label: "Approvals", icon: FaCheckDouble, color: "#81c8f3", to: "/dAttendance_Approvals", page: "Approvals" },
            { label: "Activity History", icon: FaHistory, color: "#59a8de", to: "/dAttendance_History", page: "Activity History" },
            { label: "User Interface", icon: FaDesktop, color: "#68b4e8", href: "https://dattendance.dolluzcorp.com/", page: "User Interface" },
        ],
    },
    // ── dAssist · teal ──────────────────────────────────────────────────────
    {
        id: "dAssist", label: "dAssist", icon: FaHandsHelping, color: "#51d69f", category: "dAssist",
        children: [
            { label: "Support Tickets", icon: FaTicketAlt, color: "#60e2c8", to: "/Support_Tickets", page: "Support Tickets" },
            { label: "User Interface", icon: FaDesktop, color: "#6fe9eb", href: "https://dassist.dolluzcorp.com/Tickets", page: "User Interface" },
        ],
    },
    // ── dBug · rose ─────────────────────────────────────────────────────────
    {
        id: "dBug", label: "dBug", icon: FaBug, color: "#d65184", category: "dBug",
        children: [
            { label: "Defect Tickets", icon: FaWrench, color: "#e2607f", to: "/Defect_Tickets", page: "Defect Tickets" },
            { label: "Idea Tickets", icon: FaLightbulb, color: "#eb6f7b", to: "/Idea_Tickets", page: "Idea Tickets" },
            { label: "User Interface", icon: FaDesktop, color: "#f38781", href: "https://dbug.dolluzcorp.com/Tickets", page: "User Interface" },
        ],
    },
    // ── dForms · violet ─────────────────────────────────────────────────────
    {
        id: "dForms", label: "dForms", icon: FaWpforms, color: "#7462da", category: "dForms",
        children: [
            { label: "User Interface", icon: FaDesktop, color: "#ac60e2", href: "https://dforms.dolluzcorp.com/", page: "User Interface" },
            { label: "Templates", icon: FaClone, color: "#8b6fe8", to: "/dForms/Templates", page: "Templates" },
        ],
    },
    // ── dTime · blue, with each nested group on its own related hue ─────────
    {
        id: "dTime", label: "dTime", icon: FaClock, color: "#5184d6", category: "dTime",
        children: [
            {
                id: "Configuration", label: "Configuration", icon: FaSlidersH, color: "#6268da",
                children: [
                    { label: "Project Configuration", icon: FaProjectDiagram, color: "#7560e2", to: "/Project_configuration", page: "Project Configuration" },
                    { label: "Leave Configuration", icon: FaCalendarAlt, color: "#9f6feb", to: "/Leave_configuration", page: "Leave Configuration" },
                ],
            },
            {
                id: "ShiftManagement", label: "Shift Management", icon: FaBusinessTime, color: "#51d6d0",
                children: [
                    { label: "Shifts", icon: FaHourglassHalf, color: "#60cce2", to: "/Shifts", page: "Shifts" },
                    { label: "Assign Shifts", icon: FaUserClock, color: "#6fbceb", to: "/Assign_Shifts", page: "Assign Shifts" },
                ],
            },
            {
                id: "Timesheet", label: "Timesheet", icon: FaStopwatch, color: "#51bcd6",
                children: [
                    { label: "Timesheet Approvals", icon: FaClipboardCheck, color: "#608fe2", to: "/Timesheet_approvals", page: "Timesheet Approvals" },
                ],
            },
            {
                id: "LeaveManagement", label: "Leave Management", icon: FaCalendarTimes, color: "#9056d7",
                children: [
                    { label: "Leave Dashboard", icon: FaTachometerAlt, color: "#ad60e2", to: "/Leave_dashboard", page: "Leave Dashboard" },
                    { label: "Leave Approval Levels", icon: FaSitemap, color: "#cb6feb", to: "/Leave_approval_Levels", page: "Leave Approval Levels" },
                    { label: "Leave Approvals", icon: FaCheckDouble, color: "#e681f3", to: "/Leave_approvals", page: "Leave Approvals" },
                ],
            },
            { label: "Holiday", icon: FaUmbrellaBeach, color: "#51a1d6", to: "/Holiday", page: "Holiday" },
            { label: "User Interface", icon: FaDesktop, color: "#6075e2", href: "https://dTime.dolluzcorp.com/", page: "User Interface" },
        ],
    },
    // ── dEpr · amber ────────────────────────────────────────────────────────
    {
        id: "dEpr", label: "dEpr", icon: FaClipboardList, color: "#d68d51", category: "dEpr",
        children: [
            { label: "Admin Interface", icon: FaDesktop, color: "#e2d360", href: "https://depr.dolluzcorp.com/", page: "User Interface" },
        ],
    },
    // ── dSpr · green ────────────────────────────────────────────────────────
    {
        id: "dSpr", label: "dSpr", icon: FaUserCheck, color: "#51d665", category: "dSpr",
        children: [
            { label: "Manager Allocation", icon: FaUserFriends, color: "#60e27c", to: "/Manager_Allocation", page: "Manager Allocation" },
            { label: "Cycle Scheduler", icon: FaCalendarPlus, color: "#6feb94", to: "/dSpr_CycleScheduler", page: "Cycle Scheduler" },
            { label: "Review Management", icon: FaTasks, color: "#81f3ab", to: "/dSpr_ReviewManagement", page: "Review Management" },
            { label: "Reports", icon: FaChartBar, color: "#59de93", to: "/dSpr_Reports", page: "Reports" },
            { label: "Email", icon: FaEnvelopeOpenText, color: "#68e8aa", to: "/dSpr_EmailTemplates", page: "Email Templates" },
            { label: "Email Logs", icon: FaScroll, color: "#79f1bf", to: "/dSpr_EmailLogs", page: "Email Logs" },
        ],
    },
    // ── dSlip · orange ──────────────────────────────────────────────────────
    {
        id: "dSlip", label: "dSlip", icon: FaFileInvoiceDollar, color: "#d66c51", category: "dSlip",
        children: [
            { label: "Payslip Components", icon: FaCogs, color: "#e28260", to: "/Payslip_Components", page: "Payslip Components" },
            { label: "Salary Structure", icon: FaMoneyCheckAlt, color: "#eb986f", to: "/Payslip_Salary_Structure", page: "Salary Structure" },
            { label: "Generate Payslips", icon: FaBolt, color: "#f3ad81", to: "/Payslip_Generate", page: "Generate Payslips" },
            { label: "Payslip Management", icon: FaFolderOpen, color: "#de9559", to: "/Payslip_Management", page: "Payslip Management" },
            { label: "Emp Form-16 Files", icon: FaFileAlt, color: "#e8aa68", to: "/Emp_Form16_Files", page: "Emp Form-16 Files" },
            { label: "Emp Healthcard Files", icon: FaIdCard, color: "#f1be79", to: "/Emp_Healthcard_Files", page: "Emp Healthcard Files" },
            { label: "Emp E-Letter Files", icon: FaFileSignature, color: "#eab24f", to: "/Emp_E_Letter_Files", page: "Emp E-Letter Files" },
            { label: "Reimbursements", icon: FaReceipt, color: "#e8a24f", to: "/Reimbursements", page: "Reimbursements" },
            { label: "Download History", icon: FaHistory, color: "#e0b34f", to: "/Download_History", page: "Download History" },
            { label: "User Interface", icon: FaDesktop, color: "#daa952", href: "https://dslip.dolluzcorp.com/", page: "User Interface" },
        ],
    },
    // ── dNews · fuchsia ─────────────────────────────────────────────────────
    {
        id: "dNews", label: "dNews", icon: FaMailBulk, color: "#ba51d6", category: "dNews",
        children: [
            { label: "Newsletter", icon: FaNewspaper, color: "#d160e2", to: "/dNews_Newsletter", page: "Newsletter" },
            { label: "Presets", icon: FaFileSignature, color: "#e66feb", to: "/dNews_Templates", page: "Presets" },
            { label: "Reminder", icon: FaBell, color: "#f381ee", to: "/dNews_Reminder", page: "Reminder" },
            { label: "Email List", icon: FaAddressBook, color: "#de59cd", to: "/dNews_EmailList", page: "Email List" },
            { label: "History", icon: FaHistory, color: "#e868cd", to: "/dNews_History", page: "History" },
        ],
    },
    // ── Manage Employees · indigo ───────────────────────────────────────────
    {
        id: "Employees", label: "Manage Employees", icon: FaUsers, color: "#5179d6", category: "Manage Employee",
        children: [
            { label: "Employee Center", icon: FaUserTie, color: "#606ae2", to: "/Employee_Center", page: "Employee Center" },
            { label: "Employee Configuration", icon: FaUsersCog, color: "#806feb", to: "/Employee_configuration", page: "Employee Configuration" },
        ],
    },
    // ── Access center · red ─────────────────────────────────────────────────
    {
        id: "AccessCenter", label: "Access center", icon: FaUserShield, color: "#d65165", category: "Access Center",
        children: [
            { label: "Access Configuration", icon: FaKey, color: "#e26860", to: "/Access_level_configuration", page: "Access Level Configuration" },
            { label: "App configuration", icon: FaUserLock, color: "#eb936f", to: "/Emp_App_Access", page: "App Configuration" },
        ],
    },
    // ── Inside D · lime ─────────────────────────────────────────────────────
    {
        id: "DolluzcorpCenter", label: "Inside D", icon: FaGlobe, color: "#bcd651", category: "Inside D",
        children: [
            { label: "Notification Creation", icon: FaPaperPlane, color: "#bce260", to: "/CreateNotification", page: "Notification Creation" },
            { label: "Banner Configuration", icon: FaImages, color: "#bdeb6f", to: "/Dolluzcorp_Banner_config", page: "Banner Configuration" },
            { label: "U/A Configuration", icon: FaBullhorn, color: "#bff381", to: "/Dolluzcorp_Updates_config", page: "U/A Configuration" },
            { label: "Blog Configuration", icon: FaBlog, color: "#95de59", to: "/Dolluzcorp_Blog_config", page: "Blog Configuration" },
            { label: "Policies Configuration", icon: FaGavel, color: "#97e868", to: "/Dolluzcorp_Policies_config", page: "Policies Configuration" },
            { label: "App Visibility", icon: FaTools, color: "#9cd94f", to: "/App_Visibility", page: "App Visibility" },
            { label: "Login Page Config", icon: FaIdBadge, color: "#a6e34d", to: "/Login_Banner_config", page: "Login Page Config" },
            { label: "User Interface", icon: FaDesktop, color: "#8fd94f", href: "https://inside.dolluzcorp.com/", page: "User Interface" },
        ],
    },
];

// Route → ancestor section ids, derived from NAV_CONFIG. Drives the accordion:
// navigating to a path opens exactly its ancestors and collapses everything
// else. Paths with no ancestors (Dashboard) collapse every section, which is
// the same behaviour the hand-written map had.
const PATH_TO_SECTIONS = (() => {
    const map = {};
    const walk = (items, ancestors) => {
        for (const item of items) {
            if (item.children) {
                walk(item.children, item.id ? [...ancestors, item.id] : ancestors);
            } else if (item.to) {
                map[item.to.toLowerCase()] = ancestors;
            }
        }
    };
    walk(NAV_CONFIG, []);
    return map;
})();

// True when `item` is, or contains, the page at `pathname`. Used to accent a
// collapsed section header so the current location is still discoverable in
// icon-only mode (where labels and chevrons are hidden).
function containsPath(item, pathname) {
    if (item.to) return item.to.toLowerCase() === pathname;
    return (item.children || []).some(child => containsPath(child, pathname));
}

// Open/closed state for every collapsible section, persisted as one JSON blob.
// It has to survive React state resets and is cheap enough to write on every
// toggle.
const OPEN_STATE_KEY = "nav.open";

function usePersistedOpenMap() {
    const [map, setMap] = useState(() => {
        try {
            const raw = localStorage.getItem(OPEN_STATE_KEY);
            const parsed = raw ? JSON.parse(raw) : null;
            return parsed && typeof parsed === "object" ? parsed : {};
        } catch {
            return {};
        }
    });
    const set = useCallback((next) => {
        setMap(prev => {
            const val = typeof next === "function" ? next(prev) : next;
            try { localStorage.setItem(OPEN_STATE_KEY, JSON.stringify(val)); } catch { }
            return val;
        });
    }, []);
    return [map, set];
}

function LeftNavbar({ navSize, setNavSize }) {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [openMap, setOpenMap] = usePersistedOpenMap();
    const navigate = useNavigate();
    const location = useLocation();
    const menuRef = useRef(null);
    const pathname = location.pathname.toLowerCase();

    // Accordion behaviour - when the URL changes, open only the sections that
    // own the new route and collapse every other section. Runs on every
    // pathname change (NavLink click + browser back/forward + direct URL).
    // Manual section toggles (clicking the header without navigating) still
    // work normally; the next navigation re-collapses them.
    useEffect(() => {
        const ancestors = PATH_TO_SECTIONS[pathname] || [];
        setOpenMap(Object.fromEntries(ancestors.map(id => [id, true])));
    }, [pathname, setOpenMap]);

    // Auto-scroll the active NavLink into view on every route change / hard
    // reload. Dynamic: matches whatever has the .active class, so every
    // current and future NavLink is covered with no per-page wiring.
    //
    // Why MutationObserver: the accordion effect above flips the owning
    // section open *after* this effect first runs, so the active <a> may not
    // be in the DOM yet. The observer waits until the section mounts its
    // children + react-router stamps .active, then scrolls.
    //
    // Why getBoundingClientRect: offsetTop is offsetParent-relative, which for
    // deeply nested submenu links gives meaningless values. Viewport-relative
    // rects + menu.scrollTop produces correct "position within the menu" math.
    useEffect(() => {
        let cancelled = false;
        let observer = null;
        let giveUp = null;

        const findAndScroll = () => {
            if (cancelled) return false;
            const menu = menuRef.current;
            if (!menu) return false;
            // Match by href rather than trusting .active alone, so a stale
            // class left over from the previous route can never win.
            const activeLink = [...menu.querySelectorAll("a.active")]
                .find(a => a.getAttribute("href")?.toLowerCase() === pathname);
            if (!activeLink) return false;

            const lr = activeLink.getBoundingClientRect();
            const mr = menu.getBoundingClientRect();
            const linkTopInMenu = lr.top - mr.top + menu.scrollTop;
            const target = linkTopInMenu - menu.clientHeight / 2 + activeLink.offsetHeight / 2;
            const clamped = Math.max(0, Math.min(target, menu.scrollHeight - menu.clientHeight));
            // Skip if we'd be scrolling by less than 5px - avoids pointless
            // animations on same-section navigations.
            if (Math.abs(menu.scrollTop - clamped) > 5) {
                menu.scrollTo({ top: clamped, behavior: "smooth" });
            }
            return true;
        };

        // Try immediately - covers same-section navigations where the link is
        // already mounted.
        if (findAndScroll()) return;

        observer = new MutationObserver(() => {
            if (findAndScroll()) {
                observer.disconnect();
                clearTimeout(giveUp);
            }
        });
        if (menuRef.current) {
            observer.observe(menuRef.current, {
                childList: true,
                subtree: true,
                attributes: true,
                attributeFilter: ["class"],
            });
        }

        // Safety net - if the route isn't in the navbar at all the observer
        // would otherwise run until the next route change.
        giveUp = setTimeout(() => observer && observer.disconnect(), 2000);

        return () => {
            cancelled = true;
            if (observer) observer.disconnect();
            clearTimeout(giveUp);
        };
    }, [pathname]);

    const [loggedInEmp, setLoggedInEmp] = useState(null);
    // Matrix-driven access checks. hasAccess(category, page) is true when the
    // current user's role has a 1 in that row of access_levels;
    // hasAnyAccess(category) hides a whole section when the role owns nothing
    // in it.
    const { hasAccess, hasAnyAccess } = useAccess();
    const [isDropdownHovered, setIsDropdownHovered] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const modalRef = useRef(null);
    const fileInputRef = useRef(null);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const navbarRef = useRef(null);
    const dropdownRef = useRef(null);

    // Prune NAV_CONFIG down to what this role may see, once per access change
    // rather than on every render. Top-level sections keep the original
    // hasAnyAccess(category) gate; nested groups survive only if at least one
    // descendant leaf does.
    const visibleNav = useMemo(() => {
        const prune = (items, category, depth) => {
            const out = [];
            for (const item of items) {
                const cat = item.category || category;
                if (item.children) {
                    const kids = prune(item.children, cat, depth + 1);
                    const keep = depth === 0 ? hasAnyAccess(cat) : kids.length > 0;
                    if (keep) out.push({ ...item, children: kids });
                } else if (hasAccess(cat, item.page)) {
                    out.push(item);
                }
            }
            return out;
        };
        return prune(NAV_CONFIG, null, 0);
    }, [hasAccess, hasAnyAccess]);

    // Toggle a section AND, when it's just been opened, scroll its full extent
    // (header + submenu) into view. block:"nearest" only scrolls if needed; for
    // bottom sections like Inside D this brings the last items above the
    // navbar's fold without yanking sections at the top.
    const toggleSection = useCallback((e, id) => {
        const wasOpen = !!openMap[id];
        setOpenMap(prev => ({ ...prev, [id]: !wasOpen }));
        if (wasOpen) return;   // closing - no scroll
        const group = e.currentTarget.closest(".nav-group");
        if (!group) return;
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                group.scrollIntoView({ behavior: "smooth", block: "nearest" });
            });
        });
    }, [openMap, setOpenMap]);

    const handleLogout = async () => {
        await apiFetch(`/api/login/logout`, {
            method: "POST",
            credentials: "include",
        });

        localStorage.clear();
        sessionStorage.clear();
        window.location.href = "/login";
    };

    // ✅ Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target) && // click not inside dropdown
                navbarRef.current &&
                !navbarRef.current.contains(event.target) // click not inside profile section
            ) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleCopyEmail = (email) => {
        navigator.clipboard.writeText(email)
            .then(() => dnAlert.success("Copied!", `${email} has been copied to clipboard`))
            .catch(() => dnAlert.error("Oops...", "Failed to copy email!"));
    };

    const handleNavSizeChange = (size) => {
        setNavSize(size);
        setDropdownOpen(false);
    };

    useEffect(() => {
        fetchloginedEmployees();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigate]);

    const fetchloginedEmployees = async () => {
        try {
            const res = await apiFetch(`/api/employee/logined_employee`, {
                method: 'GET',
                credentials: 'include',
            });
            const data = await res.json();

            if (data?.message === "Access Denied. No Token Provided!" ||
                data?.message === "Invalid Token") {
                navigate("/login");
                return;
            }

            if (data[0].emp_id) {
                setLoggedInEmp(data[0]);
                if ((data[0].emp_access_level !== "Admin" && data[0].emp_access_level !== "Sub Admin") || data[0].app_dAdmin === 0) {
                    await apiFetch(`/api/login/logout`, {
                        method: "POST",
                        credentials: "include",
                    });
                    localStorage.clear();
                    sessionStorage.clear();
                    navigate("/login");
                    return;
                }
            }

        } catch (err) {
            console.error("Error fetching employees:", err);
        }
    };

    const MAX_FILE_SIZE = 0.5 * 1024 * 1024; // 0.5 MB in bytes

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // ✅ Check file size
        if (file.size > MAX_FILE_SIZE) {
            dnAlert.warning("File Too Large", "Please select an image smaller than 0.5 MB.");
            // Reset selected file and input
            setSelectedFile(null);
            if (fileInputRef.current) fileInputRef.current.value = "";
            return;
        }

        setSelectedFile(file);
    };

    const handleSaveProfileImage = async () => {
        if (!selectedFile) {
            dnAlert.warning("No File Selected", "Please select a profile image before saving.");
            return;
        }

        // ✅ Optional: double-check file size before sending
        if (selectedFile.size > MAX_FILE_SIZE) {
            dnAlert.warning("File Too Large", "Please select an image smaller than 0.5 MB.");
            return;
        }

        const formDataToSend = new FormData();
        formDataToSend.append("profile", selectedFile);

        try {
            const res = await apiFetch(`/api/employee/upload-profile`, {
                method: "POST",
                body: formDataToSend,
                credentials: "include",
            });

            const data = await res.json();

            if (!res.ok) throw new Error(data.error || "Failed to upload profile image");

            dnAlert.success("Profile Updated", "Your profile image has been updated successfully!");
            setIsModalOpen(false);

            // ✅ Update logged in user state
            setLoggedInEmp(prev => ({ ...prev, emp_profile_img: data.profilePath }));

            // ✅ Reset file input
            setSelectedFile(null);
            if (fileInputRef.current) fileInputRef.current.value = null;

        } catch (err) {
            dnAlert.error("Upload Error", err.message);
        }
    };

    // Recursive renderer. `depth` feeds the --depth CSS var that indents
    // nested rows in full view and is ignored (rows centre instead) in
    // icon-only view. In icon-only view every row also carries a title so the
    // label is still reachable on hover.
    const renderItems = (items, depth) => items.map(item => {
        const Icon = item.icon;
        const key = item.id || item.to || item.href || item.label;
        const tip = navSize === "icon-only" ? item.label : undefined;
        // --icon-color tints both the glyph and the active indicator bar;
        // --depth drives the nested indent. Both are read by left_navbar.css.
        const rowStyle = { "--depth": String(depth), "--icon-color": item.color };

        if (item.children) {
            const open = !!openMap[item.id];
            const holdsActive = containsPath(item, pathname);
            const onToggle = (e) => toggleSection(e, item.id);
            return (
                <li key={key} className="nav-group">
                    <div
                        className={`nav-row nav-group-header${open ? " open" : ""}${holdsActive ? " holds-active" : ""}`}
                        style={rowStyle}
                        title={tip}
                        role="button"
                        tabIndex={0}
                        aria-expanded={open}
                        onClick={onToggle}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggle(e); }
                        }}
                    >
                        <Icon className="nav-icon" />
                        {navSize === "full" && <span className="nav-label">{item.label}</span>}
                        {/* Chevron only in full view - in the 50px rail it would
                            sit on top of the icon. */}
                        {navSize === "full" && (
                            <span className="arrow-icon">
                                {open ? <FaChevronUp /> : <FaChevronDown />}
                            </span>
                        )}
                    </div>
                    {open && <ul className="submenu">{renderItems(item.children, depth + 1)}</ul>}
                </li>
            );
        }

        if (item.href) {
            return (
                <li key={key}>
                    <a
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="nav-row nav-link"
                        style={rowStyle}
                        title={tip}
                    >
                        <Icon className="nav-icon" />
                        {navSize === "full" && <span className="nav-label">{item.label}</span>}
                        {navSize === "full" && <FaExternalLinkAlt className="nav-ext-icon" />}
                    </a>
                </li>
            );
        }

        return (
            <li key={key}>
                <NavLink
                    to={item.to}
                    className={({ isActive }) => `nav-row${isActive ? " active" : ""}`}
                    style={rowStyle}
                    title={tip}
                >
                    <Icon className="nav-icon" />
                    {navSize === "full" && <span className="nav-label">{item.label}</span>}
                </NavLink>
            </li>
        );
    });

    return (
        <div className={`left-navbar ${navSize}`}>
            <div className="navbar-header">
                {navSize !== "hidden" && (
                    <a className="navbar-brand" href="/Dashboard">
                        <img src={logo_eagle} alt="dAdmin Logo" className={`logo-img ${navSize}`} />
                        {navSize === "full" && <span className="brand-text">dAdmin</span>}
                    </a>
                )}
            </div>

            {navSize !== "hidden" && (
                <ul className="navbar-menu" ref={menuRef}>
                    {renderItems(visibleNav, 0)}
                </ul>
            )}

            <div className="logout-container">
                {/* Dropdown Arrow Above */}
                <div
                    className="view-dropdown-arrow"
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                >
                    {navSize === "full" ? <FaAngleLeft className="view-change-icon" /> : <FaAngleRight className="view-change-icon" />}

                    {dropdownOpen && (
                        <div className="view-dropdown-menu">
                            <div className="view-dropdown-item" onClick={() => handleNavSizeChange("full")}>
                                <FaGripLines /> Full View
                            </div>
                            <div className="view-dropdown-item" onClick={() => handleNavSizeChange("icon-only")}>
                                <FaBars /> Icon Only
                            </div>
                            <div className="view-dropdown-item" onClick={() => handleNavSizeChange("hidden")}>
                                <FaChevronLeft /> Hidden
                            </div>
                        </div>
                    )}
                </div>

                {/* 🔹 Logout/Profile Section */}
                {navSize !== "hidden" && (
                    <div
                        ref={navbarRef}
                        className="profile-section"
                        onClick={() => setIsDropdownOpen((prev) => !prev)}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                            cursor: "pointer",
                            justifyContent: navSize === "icon-only" ? "center" : "flex-start",
                            padding: navSize === "icon-only" ? "5px 5px" : "10px 4px",
                        }}
                    >
                        {loggedInEmp ? (
                            <>
                                {/* Profile Circle */}
                                <RoundedCircle
                                    className="profile-img"
                                    style={{
                                        backgroundColor: loggedInEmp.emp_profile_img ? 'transparent' : loggedInEmp.profile_color, color: "white",
                                        fontSize: navSize === "icon-only" ? "14px" : "18px",
                                        fontWeight: "bold",
                                        width: navSize === "icon-only" ? "30px" : "35px",
                                        height: navSize === "icon-only" ? "30px" : "35px",
                                        backgroundImage: loggedInEmp.emp_profile_img
                                            ? loggedInEmp.emp_profile_img.startsWith("data:")
                                                ? `url(${loggedInEmp.emp_profile_img})`
                                                : `url(${API_BASE}/${loggedInEmp.emp_profile_img.replace(/\\/g, "/")})`
                                            : undefined,
                                        backgroundSize: "cover",
                                        backgroundPosition: "center",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    {!loggedInEmp.emp_profile_img && loggedInEmp.profile_letters}
                                </RoundedCircle>

                                {/* Show text + arrows only if full view */}
                                {navSize === "full" && (
                                    <>
                                        <div className="profile-text">
                                            <span className="profile-name"
                                                title={`${loggedInEmp.emp_first_name} ${loggedInEmp.emp_last_name}`}
                                            >
                                                {loggedInEmp.emp_first_name + " " + loggedInEmp.emp_last_name}
                                            </span>
                                            <span className="profile-org" style={{ fontStyle: "italic" }}>
                                                Your People, Your Power
                                            </span>
                                        </div>

                                        <div className="profile-arrow-wrapper">
                                            <i className="fa fa-chevron-up profile-arrow"></i>
                                            <i className="fa fa-chevron-down profile-arrow"></i>
                                        </div>
                                    </>
                                )}
                            </>
                        ) : (
                            "Loading..."
                        )}
                    </div>
                )}

                {isDropdownOpen && loggedInEmp && (
                    <DropdownMenu className="dropdown-menu" $isOpen={isDropdownOpen} $navSize={navSize} ref={dropdownRef} >
                        <DropdownProfile>
                            <RoundedCircle
                                className="profile-img"
                                style={{
                                    backgroundColor: loggedInEmp.emp_profile_img ? 'transparent' : loggedInEmp.profile_color,
                                    color: 'white',
                                    fontSize: '18px',
                                    fontWeight: 'bold',
                                    cursor: 'pointer',
                                    backgroundImage: loggedInEmp.emp_profile_img
                                        ? loggedInEmp.emp_profile_img.startsWith("data:")
                                            ? `url(${loggedInEmp.emp_profile_img})`
                                            : `url(${API_BASE}/${loggedInEmp.emp_profile_img.replace(/\\/g, "/")})`
                                        : undefined,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                                onMouseEnter={() => setIsDropdownHovered(true)}
                                onMouseLeave={() => setIsDropdownHovered(false)}
                                onClick={() => { setIsModalOpen(true); }}
                            >
                                {/* Show camera on hover, otherwise letters if no image */}
                                {isDropdownHovered ? (
                                    <FaCamera color="white" />
                                ) : (
                                    !loggedInEmp.emp_profile_img && loggedInEmp.profile_letters
                                )}
                            </RoundedCircle>

                            <div
                                className="profile-text"
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    minWidth: 0
                                }}
                            >
                                <DropdownProfileName
                                    title={`${loggedInEmp.emp_first_name} ${loggedInEmp.emp_last_name}`}
                                >
                                    {loggedInEmp.emp_first_name + " " + loggedInEmp.emp_last_name}
                                </DropdownProfileName>
                                <DropdownProfileOrg style={{ fontStyle: 'italic' }}>
                                    Your People, Your Power
                                </DropdownProfileOrg>
                            </div>
                        </DropdownProfile>

                        <DropdownItem
                            style={{ display: 'flex', alignItems: 'center', gap: '15px', cursor: 'pointer' }}
                            onClick={() => handleCopyEmail("Help@dFroms.io")}
                        >
                            <img
                                src={help_desk}
                                alt="Help Desk"
                                style={{ width: "20px", height: "20px", objectFit: "contain" }}
                            />
                            admin@dolluzcorp.com
                        </DropdownItem>
                        <DropdownItem
                            style={{ display: 'flex', alignItems: 'center', gap: '15px', cursor: 'pointer' }}
                            href="/login?changePassword"
                        >
                            <i className="fa fa-key"></i> Change Password
                        </DropdownItem>
                        <DropdownDivider />
                        <DropdownItem
                            onClick={handleLogout}
                            style={{ display: 'flex', alignItems: 'center', gap: '15px', cursor: 'pointer', color: "red" }}
                        >
                            <FaSignOutAlt className="logout-icon" style={{ marginLeft: "6px" }} />
                            Logout
                        </DropdownItem>
                    </DropdownMenu>
                )}

                {isModalOpen &&
                    createPortal(
                        <div className="profile-modal-overlay">
                            <div className="profile-modal" ref={modalRef}>
                                <button
                                    className="profile-modal-close"
                                    onClick={() => setIsModalOpen(false)}
                                >
                                    ✖
                                </button>

                                <h3 className="profile-modal-title">Update Profile Image</h3>

                                <div style={{ display: 'flex', justifyContent: 'center', margin: '20px 0' }}>
                                    {selectedFile ? (
                                        <img
                                            src={URL.createObjectURL(selectedFile)}
                                            alt="Preview"
                                            className="profile-image-preview"
                                        />
                                    ) : (
                                        <FaCamera size={60} color="#555" />
                                    )}
                                </div>

                                <div style={{ position: "relative", width: "100%" }}>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/*"
                                        onChange={handleFileChange}
                                        style={{ width: "100%", paddingRight: selectedFile ? "30px" : "0" }}
                                    />

                                    {selectedFile && (
                                        <span
                                            onClick={() => {
                                                setSelectedFile(null);
                                                if (fileInputRef.current) fileInputRef.current.value = "";
                                            }}
                                            style={{
                                                position: "absolute",
                                                right: "8px",
                                                top: "50%",
                                                transform: "translateY(-50%)",
                                                cursor: "pointer",
                                                fontSize: "16px",
                                                color: "#888",
                                            }}
                                        >
                                            ✖
                                        </span>
                                    )}
                                </div>

                                <div className="profile-modal-buttons">
                                    <button
                                        className="profile-modal-save"
                                        onClick={handleSaveProfileImage}
                                        disabled={!selectedFile}
                                        style={{
                                            cursor: selectedFile ? "pointer" : "not-allowed",
                                            opacity: selectedFile ? 1 : 0.5
                                        }}
                                    >
                                        Save
                                    </button>
                                </div>
                            </div>
                        </div>,
                        document.body // ✅ Portal target - renders outside navbar
                    )
                }

            </div>
        </div >
    );
}

export default LeftNavbar;
